<template>
    <div class="rule-builder">
        <div v-if="label" class="mb-2">
            <label class="block text-sm font-medium leading-5 text-gray-700">
                {{ label }}
            </label>
            <p v-if="description" class="mt-1 text-sm text-gray-500">
                {{ description }}
            </p>
        </div>

        <div class="space-y-3">
            <div
                v-for="(rule, index) in localRules"
                :key="index"
                class="flex items-start space-x-2 rounded-md border border-gray-300 bg-white p-4"
            >
                <div class="flex-1 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div
                        v-for="field in fields"
                        :key="field.key"
                        :class="field.colSpan ? `sm:col-span-${field.colSpan}` : ''"
                    >
                        <label
                            :for="`${field.key}-${index}`"
                            class="block text-xs font-medium text-gray-700"
                        >
                            {{ field.label }}
                            <span v-if="field.required" class="text-red-500">*</span>
                        </label>

                        <select
                            v-if="field.type === 'select'"
                            :id="`${field.key}-${index}`"
                            v-model="rule[field.key]"
                            @change="emitUpdate"
                            class="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                            :class="{ 'border-red-300': hasError(index, field.key) }"
                        >
                            <option
                                v-for="option in field.options"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>

                        <div v-else-if="field.type === 'currency'" class="relative mt-1">
                            <div
                                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                            >
                                <span class="text-gray-500 sm:text-sm">
                                    {{ field.currencySymbol || '£' }}
                                </span>
                            </div>
                            <currency-input
                                :id="`${field.key}-${index}`"
                                v-model="rule[field.key]"
                                @input="emitUpdate"
                                :currency="null"
                                :value-as-integer="true"
                                :allow-negative="false"
                                :placeholder="field.placeholder"
                                class="block w-full rounded-md border-gray-300 py-2 pl-7 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                                :class="{ 'border-red-300': hasError(index, field.key) }"
                            />
                        </div>

                        <input
                            v-else-if="field.type === 'number'"
                            :id="`${field.key}-${index}`"
                            v-model.number="rule[field.key]"
                            @input="emitUpdate"
                            type="number"
                            :min="field.min"
                            :max="field.max"
                            :step="field.step || 1"
                            :placeholder="field.placeholder"
                            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                            :class="{ 'border-red-300': hasError(index, field.key) }"
                        />

                        <input
                            v-else
                            :id="`${field.key}-${index}`"
                            v-model="rule[field.key]"
                            @input="emitUpdate"
                            :type="field.type || 'text'"
                            :placeholder="field.placeholder"
                            class="mt-1 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                            :class="{ 'border-red-300': hasError(index, field.key) }"
                        />

                        <p
                            v-if="hasError(index, field.key)"
                            class="mt-1 text-xs text-red-600"
                        >
                            {{ getErrorMessage(index, field.key) }}
                        </p>
                    </div>
                </div>

                <button
                    @click="removeRule(index)"
                    type="button"
                    class="mt-6 flex-shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :aria-label="`Remove rule ${index + 1}`"
                >
                    <svg
                        class="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            <div v-if="localRules.length === 0" class="text-center py-6">
                <p class="text-sm text-gray-500">
                    {{ emptyMessage || 'No rules defined yet.' }}
                </p>
            </div>

            <button
                @click="addRule"
                type="button"
                class="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
                <svg
                    class="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4v16m8-8H4"
                    />
                </svg>
                {{ addButtonText || 'Add Rule' }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RuleBuilder',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        fields: {
            type: Array,
            required: true
        },
        label: {
            type: String,
            default: ''
        },
        description: {
            type: String,
            default: ''
        },
        addButtonText: {
            type: String,
            default: 'Add Rule'
        },
        emptyMessage: {
            type: String,
            default: ''
        },
        defaultRule: {
            type: Object,
            default: () => ({})
        },
        validate: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            localRules: [],
            errors: {}
        };
    },
    watch: {
        value: {
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.localRules)) {
                    this.localRules = newValue ? [...newValue] : [];
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        addRule() {
            const newRule = this.createDefaultRule();
            this.localRules.push(newRule);
            this.emitUpdate();
        },
        removeRule(index) {
            this.localRules.splice(index, 1);
            delete this.errors[index];
            this.emitUpdate();
        },
        createDefaultRule() {
            const rule = { ...this.defaultRule };
            
            this.fields.forEach(field => {
                if (!(field.key in rule)) {
                    if (field.type === 'number' || field.type === 'currency') {
                        rule[field.key] = field.default !== undefined ? field.default : 0;
                    } else if (field.type === 'select' && field.options && field.options.length > 0) {
                        rule[field.key] = field.default !== undefined ? field.default : field.options[0].value;
                    } else {
                        rule[field.key] = field.default !== undefined ? field.default : '';
                    }
                }
            });
            
            return rule;
        },
        emitUpdate() {
            this.validateRules();
            this.$emit('input', this.localRules);
        },
        validateRules() {
            if (!this.validate) {
                this.errors = {};
                return;
            }

            const newErrors = {};
            this.localRules.forEach((rule, index) => {
                const ruleErrors = this.validate(rule, index, this.localRules);
                if (ruleErrors && Object.keys(ruleErrors).length > 0) {
                    newErrors[index] = ruleErrors;
                }
            });
            this.errors = newErrors;
        },
        hasError(index, fieldKey) {
            return this.errors[index] && this.errors[index][fieldKey];
        },
        getErrorMessage(index, fieldKey) {
            return this.errors[index] && this.errors[index][fieldKey];
        },
        isValid() {
            this.validateRules();
            return Object.keys(this.errors).length === 0;
        }
    }
};
</script>

<style scoped>
.rule-builder {
    width: 100%;
}
</style>
